import { useEffect, useState } from 'react';

import { useTranslation } from 'react-i18next';

import {
  ActivationCensusAsyncStep,
  ActivationCensusErrorDataManyStep,
  ActivationCensusErrorMinimumStep,
  ActivationCensusErrorServiceStep,
  ActivationCensusPriceMismatchStep,
  ActivationCensusSuccessStep
} from './steps';
import { MIN_EMPLOYEES_FOR_QUOTES } from '../../../constants/quotes';
import {
  ACTIVATION_ACCEPT_NEW_PRICE,
  ACTIVATION_APPLY_CENSUS,
  ACTIVATION_CENSUS_BACKGROUND,
  ACTIVATION_CENSUS_CLOSE,
  ACTIVATION_CENSUS_HELP,
  ACTIVATION_DOWNLOAD_TEMPLATE,
  ACTIVATION_REJECT_NEW_PRICE,
  ACTIVATION_REMOVE_CENSUS,
  ACTIVATION_RETRY_CENSUS,
  ACTIVATION_VALIDATE_CENSUS,
  CENSUS_RESULT,
  CENSUS_VALIDATION_RESULT,
  CLICK_ACTION,
  VIEW_ACTION
} from '../../../constants/tagManager';
import type { ActivationCensusStep } from '../../../hooks/useActivationCensusFlow/activationCensus';
import type { ActivationCensusFlow } from '../../../hooks/useActivationCensusFlow/useActivationCensusFlow';
import QuotesService from '../../../services/api/quotes/quotes';
import TagManagerService from '../../../services/gtm/tagManagerService';
import { TRANSLATIONS_PROSPECTS } from '../../../translations/prospects';
import { censusVariantFor } from '../../../utils/quotes/quotes';
import CustomButton from '../../Buttons/CustomButton/CustomButton';
import ModalCensus from '../ModalCensus/ModalCensus';

import './ActivationCensusModal.scss';

const MODAL = TRANSLATIONS_PROSPECTS.profile.activationSetup.censusModal;

const RESULT_BY_STEP: Partial<Record<ActivationCensusStep, string>> = {
  success: CENSUS_RESULT.success,
  'error-data-many': CENSUS_RESULT.dataErrors,
  'error-minimum': CENSUS_RESULT.belowMinimum,
  'price-mismatch': CENSUS_RESULT.priceMismatch,
  'error-service': CENSUS_RESULT.serviceError
};

export interface ActivationCensusApplyPayload {
  filename: string;
}

export interface ActivationCensusModalProps {
  open: boolean;
  brokerId: string | null;
  /** Owned by the parent so the flow survives closing the modal mid-validation. */
  flow: ActivationCensusFlow;
  quoteName?: string;
  /**
   * App-only prospects/clients have no carrier: the success step drops the carrier mention and the
   * below-minimum step uses the carrier-free copy for its (relaxed) headcount floor.
   */
  isAppOnly?: boolean;
  onClose: () => void;
  /** Persists the census on the quote (`applied: true`). Rejecting keeps the modal open. */
  onApply: (payload: ActivationCensusApplyPayload) => void | Promise<void>;
}

export default function ActivationCensusModal({
  open,
  brokerId,
  flow,
  quoteName,
  isAppOnly,
  onClose,
  onApply
}: Readonly<ActivationCensusModalProps>) {
  const { t } = useTranslation();
  const [applying, setApplying] = useState(false);

  const result = RESULT_BY_STEP[flow.step];
  useEffect(() => {
    if (!open || !result) return;
    TagManagerService.pushEvent(VIEW_ACTION, CENSUS_VALIDATION_RESULT, { result });
  }, [open, result]);

  useEffect(() => {
    if (open && flow.step === 'error-data-many' && !flow.errorsFile) flow.refreshErrorsFile();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, flow.step, flow.errorsFile]);

  if (!open) return null;

  const handleDownloadTemplate = async () => {
    if (!brokerId) return;
    try {
      TagManagerService.pushEvent(CLICK_ACTION, ACTIVATION_DOWNLOAD_TEMPLATE);
      const { url } = await QuotesService.getCensusTemplate(brokerId, censusVariantFor(!!isAppOnly));
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch {
      // Silently fail — template download is non-critical.
    }
  };

  const handleApply = async () => {
    TagManagerService.pushEvent(CLICK_ACTION, ACTIVATION_APPLY_CENSUS);
    setApplying(true);
    try {
      await onApply({ filename: flow.fileName ?? '' });
      onClose();
    } catch {
      // Left on `success` so the broker can hit Apply again.
    } finally {
      setApplying(false);
    }
  };

  const handleClose = () => {
    TagManagerService.pushEvent(CLICK_ACTION, ACTIVATION_CENSUS_CLOSE);
    onClose();
  };

  const handleKeepInBackground = () => {
    TagManagerService.pushEvent(CLICK_ACTION, ACTIVATION_CENSUS_BACKGROUND);
    onClose();
  };

  const handleCancel = () => flow.reset();

  const handleUpload = () => {
    flow.handleUpload();
    TagManagerService.pushEvent(CLICK_ACTION, ACTIVATION_VALIDATE_CENSUS);
  };

  const handleToggleInstructions = () => {
    TagManagerService.pushEvent(CLICK_ACTION, ACTIVATION_CENSUS_HELP);
  };

  const handleClearFile = () => {
    TagManagerService.pushEvent(CLICK_ACTION, ACTIVATION_REMOVE_CENSUS);
    flow.setFile(null);
  };

  const handleRetry = () => {
    TagManagerService.pushEvent(CLICK_ACTION, ACTIVATION_RETRY_CENSUS);
    flow.reset();
  };

  const handleAcceptNewPrice = () => {
    TagManagerService.pushEvent(CLICK_ACTION, ACTIVATION_ACCEPT_NEW_PRICE);
    flow.acceptNewPrice();
  };

  const handleRejectNewPrice = () => {
    TagManagerService.pushEvent(CLICK_ACTION, ACTIVATION_REJECT_NEW_PRICE);
    flow.rejectNewPrice();
  };

  const TITLE_BY_STEP: Partial<Record<typeof flow.step, string>> = {
    validating: t(MODAL.validatingTitle),
    processing: t(MODAL.processingTitle),
    success: t(MODAL.successTitle),
    'error-data-many': t(MODAL.errorManyTitle),
    'error-minimum': t(MODAL.errorMinimumTitle),
    'price-mismatch': t(MODAL.priceMismatchTitle),
    'error-service': t(MODAL.errorServiceTitle)
  };
  const withQuoteName = (stepTitle: string) => (quoteName ? `${stepTitle} · ${quoteName}` : stepTitle);
  const stepTitle = TITLE_BY_STEP[flow.step];
  const title = stepTitle ? withQuoteName(stepTitle) : '';
  const uploadTitle = withQuoteName(t(MODAL.uploadTitle));

  const instructions = [
    { title: t(MODAL.instruction1Title) },
    { title: t(MODAL.instruction2Title) },
    { title: t(MODAL.instruction3Title), sub: t(MODAL.instruction3Sub) },
    { title: t(MODAL.instruction4Title), sub: t(MODAL.instruction4Sub) }
  ];

  const closeButton = (
    <CustomButton variant="default" size="m" onClick={handleClose}>
      {t(MODAL.close)}
    </CustomButton>
  );

  const renderBody = () => {
    switch (flow.step) {
      case 'validating':
        return <ActivationCensusAsyncStep title={t(MODAL.validatingTitle)} hint={t(MODAL.validatingHint)} />;
      case 'processing':
        return <ActivationCensusAsyncStep title={t(MODAL.processingTitle)} hint={t(MODAL.processingHint)} />;
      case 'success':
        return <ActivationCensusSuccessStep employeesValidated={flow.summary?.valid} isAppOnly={isAppOnly} />;
      case 'error-data-many':
        return (
          <ActivationCensusErrorDataManyStep
            rowsWithErrors={flow.summary?.withErrors ?? 0}
            formatErrors={flow.summary?.formatErrors ?? 0}
            emptyCellErrors={flow.summary?.emptyCellErrors ?? 0}
            errorsFile={flow.errorsFile}
          />
        );
      case 'error-minimum':
        return (
          <ActivationCensusErrorMinimumStep
            employeeCount={flow.summary?.total ?? 0}
            minimum={isAppOnly ? 1 : MIN_EMPLOYEES_FOR_QUOTES}
            isAppOnly={isAppOnly}
          />
        );
      case 'price-mismatch':
        return flow.priceMismatch ? <ActivationCensusPriceMismatchStep payload={flow.priceMismatch} /> : null;
      case 'error-service':
        return <ActivationCensusErrorServiceStep />;
      default:
        return null;
    }
  };

  const renderFooter = () => {
    switch (flow.step) {
      case 'validating':
      case 'processing':
        return (
          <>
            <CustomButton variant="default" size="m" onClick={handleCancel}>
              {t(MODAL.cancel)}
            </CustomButton>
            <CustomButton variant="default" size="m" onClick={handleKeepInBackground}>
              {t(MODAL.keepInBackground)}
            </CustomButton>
          </>
        );
      case 'success':
        return (
          <>
            {closeButton}
            <CustomButton variant="primary" size="m" loading={applying} onClick={handleApply}>
              {t(MODAL.apply)}
            </CustomButton>
          </>
        );

      case 'error-data-many':
      case 'error-minimum':
        return (
          <>
            {closeButton}
            <CustomButton variant="primary" size="m" onClick={flow.reset}>
              {t(flow.step === 'error-data-many' ? MODAL.uploadCorrectedFile : MODAL.uploadNewCensus)}
            </CustomButton>
          </>
        );
      case 'price-mismatch':
        return (
          <>
            <CustomButton variant="default" size="m" onClick={handleRejectNewPrice}>
              {t(MODAL.rejectNewPrice)}
            </CustomButton>
            <CustomButton variant="primary" size="m" onClick={handleAcceptNewPrice}>
              {t(MODAL.acceptNewPrice)} →
            </CustomButton>
          </>
        );
      case 'error-service':
        return (
          <>
            {closeButton}
            <CustomButton variant="primary" size="m" onClick={handleRetry}>
              {t(MODAL.tryAgain)}
            </CustomButton>
          </>
        );
      default:
        return null;
    }
  };

  return (
    <ModalCensus
      open={open}
      onClose={handleClose}
      title={title}
      uploadTitle={uploadTitle}
      step={flow.step}
      file={flow.file}
      onFileSelect={flow.handleFileSelect}
      onClear={handleClearFile}
      onUpload={handleUpload}
      uploadDisabled={!flow.file}
      onDownloadTemplate={handleDownloadTemplate}
      onRetryUpload={handleRetry}
      onToggleInstructions={handleToggleInstructions}
      instructions={instructions}
      renderFooter={renderFooter}
    >
      {renderBody()}
    </ModalCensus>
  );
}
